import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Briefcase, Wrench, Megaphone, FileSignature, CalendarCheck, UserCheck, Globe, CalendarClock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MANT_STYLES = {
  programado: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  completado: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  cancelado: 'bg-zinc-700/30 text-zinc-500 border-zinc-600/30',
};

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-white/[0.04]">
      <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
      <span className="text-xs text-zinc-500 w-44 flex-shrink-0">{label}</span>
      <span className="text-sm text-white">{value || <span className="text-zinc-600">No registrado</span>}</span>
    </div>
  );
}

export function InfoComercialCliente({ trialId }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/comercial/trials/${trialId}/info-comercial`, { withCredentials: true });
        setInfo(r.data);
      } catch (err) {
        toast.error('Error cargando la información comercial');
      } finally {
        setLoading(false);
      }
    })();
  }, [trialId]);

  if (loading) return <p className="text-sm text-zinc-500 py-6 text-center">Cargando trazabilidad comercial...</p>;
  if (!info) return null;

  return (
    <div className="space-y-6" data-testid="info-comercial-cliente">
      <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
        <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-4">
          <Briefcase className="w-4 h-4 text-zinc-500" />Trazabilidad Comercial
        </h3>
        <div className="space-y-0.5">
          <InfoRow icon={UserCheck} label="Comercial responsable" value={info.comercial_responsable} />
          <InfoRow icon={Wrench} label="Técnico instalador" value={info.tecnico_instalador} />
          <InfoRow icon={Megaphone} label="Canal de captación" value={info.canal_captacion} />
          <InfoRow icon={FileSignature} label="Contrato creado por" value={info.contrato ? `${info.contrato.creado_por} (${info.contrato.numero})` : null} />
          <InfoRow icon={CalendarClock} label="Fecha creación contrato" value={info.contrato?.fecha_creacion?.slice(0, 10)} />
          <InfoRow icon={CalendarCheck} label="Alta de suscripción" value={info.suscripcion ? `${info.suscripcion.fecha_alta} · activada por ${info.suscripcion.activada_por}` : 'Sin suscripción activa'} />
          <InfoRow icon={Globe} label="Portal Cliente" value={
            info.portal_cliente?.tiene_acceso
              ? `Con acceso (${info.portal_cliente.estado}) · correo ${info.portal_cliente.email_verificado ? 'verificado' : 'sin verificar'}`
              : 'Sin acceso todavía'
          } />
        </div>
      </div>

      <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
        <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-4">
          <CalendarCheck className="w-4 h-4 text-zinc-500" />Mantenimientos ({info.mantenimientos.length})
        </h3>
        {info.mantenimientos.length === 0 ? (
          <p className="text-xs text-zinc-600">Sin mantenimientos programados. Se crean automáticamente al activar una suscripción (a los 12 meses).</p>
        ) : (
          <div className="space-y-2">
            {info.mantenimientos.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]" data-testid={`mantenimiento-${m.id}`}>
                <div>
                  <p className="text-sm text-white">
                    {m.tipo === 'preventivo_anual' ? 'Mantenimiento preventivo anual' : m.tipo}
                    <span className="text-xs text-zinc-500 ml-2">{m.fecha_prevista?.slice(0, 10)}</span>
                  </p>
                  <p className="text-[10px] text-zinc-600">{m.nota}</p>
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border flex-shrink-0 ${MANT_STYLES[m.estado] || MANT_STYLES.programado}`}>
                  {m.estado.charAt(0).toUpperCase() + m.estado.slice(1)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
